import http from "./HttpService";
import { clearStore, getLookupEntries, saveLookupEntriesWithMeta, } from "../services/db";
import { EVENTS_LISTING_STORE_NAME, } from "../constants";
import { getUserId } from "../security";

const endpoint = `/events`;
const pwaEndpoint = `/v2/events`;

export const createEventVenue = async (data) => {
  return http.post(`/events/venue/create`, data);
};

export const getEventVenues = (total = 20) => {
  return http.get(`${pwaEndpoint}/venue/list?total=${total}`)
};

export const genEventVenueById = (id) => {
  return http.get(`${endpoint}/venue/${id}`)
};

/** Create Event */
export const createEvent = (data) => {
  return http.post(`${pwaEndpoint}`, data);
};

/** Update Existing Event */
export const updateEvent = (id, data) => {
  return http.put(`${endpoint}/${id}`, data);
};

/** Get Single Event */
export const getEventById = (id) => {
  return http.get(`${endpoint}/${id}`);
};

/** GET EVENTS (Offline-First with Fallback) */
export const getEvents = async ({ category = "all" }) => {
  let networkError = null;

  // session-aware cleanup
  const LAST_USER_KEY = "__last_user_id__";
  const currentUser = getUserId();
  const lastUser = localStorage.getItem(LAST_USER_KEY);

  if (currentUser !== lastUser) {
    console.warn("[Events] User changed → clearing event cache");

    await clearStore(EVENTS_LISTING_STORE_NAME);
    localStorage.setItem(LAST_USER_KEY, currentUser || "");
  }

  // Attempt network first (if online)
  if (navigator.onLine) {
    try {
      const res = await http.get(
        `${pwaEndpoint}/view?category=${category}`
      );

      if (res?.status === 200) {
        const events = res.data || [];

        // Update cache (non-blocking)
        if (events.length > 0) {
          saveLookupEntriesWithMeta( EVENTS_LISTING_STORE_NAME, events, Date.now().toString());
        }

        return {
          status: 200,
          data: events,
          source: "network",
        };
      }

      networkError = new Error(`Unexpected status ${res?.status}`);
    } catch (err) {
      console.warn("Failed to fetch events from network, falling back to cache", err);
      networkError = err;
    }
  }

  // Fallback to cache
  try {
    const cachedEvents = await getLookupEntries(EVENTS_LISTING_STORE_NAME);

    return {
      status: 200,
      data: cachedEvents || [],
      source: "cache",
      fallbackFrom: navigator.onLine ? "network-error" : "offline",
    };
  } catch (cacheErr) {
    console.error("Failed to retrieve events from cache", cacheErr);

    return {
      status: 500,
      data: [],
      source: "error",
      error: networkError || cacheErr,
    };
  }
};

/** Delete Event */
export const deleteEvent = (id) => {
  return http.delete(`${endpoint}/${id}`);
};

const updateEventStatus = async ({ action, notes, eventId }) => {
  const response = await http.put(`${endpoint}/ratification/${eventId}`,
    {
      action: action,
      notes: notes || null
    }
  );
  return response;
};

/** Get events partners */
const getOrganizations = async ({ first, max, searchText = undefined, ownRecords = false }) => {
  const response = await http.get(`${pwaEndpoint}/partner/view?offset=${first}&limit=${max}&searchText=${searchText}&ownRecords=${ownRecords}`);
  return response;
}

const addPartnership = async (
  partner, address
) => {
  const entity = "partner";
  let msg = "";

  const {id = -1} = partner;
  const {id: addressId = -1} = address;
  const payload = {
      "id": id,
      "name": partner.name,
      "acronym": partner.acronym,
      "initiationDate": new Date().toISOString(),
      "lastEngagementDate": new Date().toISOString(),
      "telephoneNumber": partner.contact,
      "email": partner.email,
      "archived": false,
      "representatives": [],
      "contributions": partner.contributions,
      "otherContribution": partner.otherContribution,
      "partnerType": partner.partnerType,
      "locality": partner.address,
      "active": true,
      "reportedOn": true,
      "address": {
        "id": addressId,
        "country": address.country,
        "province": address.province,
        "cityOrTown": address.cityOrTown,
        "street": address.street,
        "postalCode": address.postalCode,
        "x": address?.x,
        "y": address?.y,
      }
    };

  if (id === -1) { // New Partner
    const response = await http.post(
      `${pwaEndpoint}/partner/create`,
      payload
    );
    return response;
  }

  const response = await http.put(
    `${endpoint}/partner/${id}`,
    payload,
  );
  return response;
};


const getPartnerDetails = async (partnerId) => {
  const response = await http.get(`${endpoint}/partner/${partnerId}`);
  return response;
};


const checkEmailExists = async (email) => {

  const response = await http.get(`${endpoint}/email/valid/${email}`, {});
  return response;
};

const addIndividualParticipant = async (transaction, params = {}) => {

  const { eventId, payload, isOnline, id} = transaction;
  const response = await http.post(`${endpoint}/participant/${eventId}`,
    payload ?? transaction?.attendee,
    {
      params: {
        online: isOnline,
        signature: id,
        ...params,
      },
    }
  );
  return response;
};

const addScienceFacilitator = async (transaction, params = {}) => {

  const { eventId, facilitator, isOnline, id} = transaction;
  const response = await http.post(`${endpoint}/science-communicator/${eventId}`,
    facilitator,
    {
      params: {
        online: isOnline,
        signature: id,
        ...params,
      },
    }
  );
  return response;
};

const addGroupParticipant = async (transaction, params = {}) => {

  const { eventId, payload, isOnline, id } = transaction;
  const response = await http.post(`${endpoint}/participants/group/${eventId}`,
    payload,
    {
      params: {
        online: isOnline,
        signature: id,
        ...params,
      },
    }
  );
  return response;
};

const getEventParticipants = async (eventId) => {
  const response = await http.get(`${pwaEndpoint}/participants/${eventId}`);
  return response;
};

const getAdministratorsForEvent = async (eventId) => {
  const response = await http.get(`${pwaEndpoint}/facilitator/${eventId}`);
  return response;
}

const createAdministratorsForEvent = async (eventId, admins) => {
  const response = await http.put(`${endpoint}/facilitator/${eventId}`, admins);
  return response;
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  createEvent,
  updateEvent,
  getEventById,
  getEvents,
  deleteEvent,
  updateEventStatus,
  getOrganizations,
  addPartnership,
  getPartnerDetails,
  checkEmailExists,
  createEventVenue,
  getEventVenues,
  genEventVenueById,
  addIndividualParticipant,
  addScienceFacilitator,
  addGroupParticipant,
  getAdministratorsForEvent,
  createAdministratorsForEvent,
  getEventParticipants
};

